import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Gauge, Ticket, ArrowRight, CreditCard } from "lucide-react";
import { toast } from "sonner";

const OPERATIONAL = [
  ["ai_interview", "AI interviews"],
  ["research", "Live market research"],
  ["validation", "Validation experiments"],
  ["businesses", "Active businesses"],
  ["websites", "Live websites"],
  ["seats", "Team seats"],
];
const PLANNED = [
  ["posts", "Social posts"],
  ["articles", "SEO articles"],
  ["emails", "Emails"],
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

function Meter({ label, used, limit, planned }) {
  const unlimited = limit === -1;
  const pct = unlimited || !limit ? 0 : Math.min(100, (used / limit) * 100);
  return (
    <div className={`card-donkey rounded-xl p-5 ${planned ? "opacity-70" : ""}`} data-testid={`usage-meter-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-donkey-warm/90">
          {label}
          {planned && <span className="ml-2 text-[10px] uppercase tracking-wider text-donkey-amber border border-donkey-amber/40 rounded px-1.5 py-0.5">Provider not connected</span>}
        </span>
        <span className="font-mono text-xs text-donkey-grey">
          {unlimited ? "Unlimited" : `${used} / ${limit || 0}`}
        </span>
      </div>
      {!unlimited && (
        <div className="h-1.5 w-full bg-white/8 rounded-full overflow-hidden">
          <div className="h-full bg-donkey-orange" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

export default function Usage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    api.get("/me/entitlement").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  const openBilling = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      if (data?.url) { window.location.href = data.url; return; }
      toast.error("Couldn't open the billing portal. Please try again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open the billing portal. Please try again in a moment.");
    } finally {
      setPortalBusy(false);
    }
  };

  if (data === null) return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-donkey-orange" /></div>;
  if (!data) return <div className="mx-auto max-w-3xl px-5 py-16 text-donkey-grey">Could not load your plan.</div>;

  const ent = data.entitlement;
  const usage = data.usage;
  const counts = usage.counts || {};
  const hasBilling = ent.entitlement_source === "paid" || ent.entitlement_source === "both";

  return (
    <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10">
      <div className="flex items-center gap-2 mb-2">
        <Gauge className="w-6 h-6 text-donkey-orange" />
        <h1 className="font-heading font-bold text-3xl tracking-tight text-donkey-warm">Plan & usage</h1>
      </div>
      <p className="text-donkey-grey mb-8 text-sm">An honest view of what you have and what you've used this period.</p>

      {/* Plan summary */}
      <div className="card-donkey rounded-2xl p-6 mb-8" data-testid="plan-summary">
        {ent.complimentary && (
          <div className="mb-5 flex flex-wrap items-center gap-2 rounded-lg bg-donkey-orange/10 border border-donkey-orange/30 px-4 py-3" data-testid="comp-access-banner">
            <span className="text-xs font-semibold uppercase tracking-wider text-donkey-orange border border-donkey-orange/50 rounded px-2 py-0.5">Complimentary access</span>
            <span className="text-sm text-donkey-warm">You have {ent.complimentary.tier_name} at no charge
              {ent.complimentary.no_expiry ? " (no expiry)" : ent.complimentary.expiry_date ? ` until ${fmtDate(ent.complimentary.expiry_date)}` : ""}.
              It will not renew and you won't be charged. When it ends, your data and reports are kept and access returns to your own plan.</span>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div>
            <p className="text-xs uppercase tracking-wider text-donkey-grey mb-1">Current plan</p>
            <p className="font-heading font-semibold text-lg text-donkey-warm" data-testid="current-plan">{ent.effective_plan_name || ent.plan_name}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-donkey-grey mb-1">Billing</p>
            <p className="text-donkey-warm capitalize">{ent.interval ? `${ent.interval}ly` : (ent.plan === "free" ? "—" : "one-off")}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-donkey-grey mb-1">Status</p>
            <p className="text-donkey-warm capitalize">{ent.status === "none" ? "free" : ent.status}{ent.read_only && " (read-only)"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-donkey-grey mb-1">{ent.status === "cancelled" ? "Ends" : "Renews / expires"}</p>
            <p className="text-donkey-warm">{fmtDate(ent.renews_at || ent.grace_until)}</p>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-2 text-sm text-donkey-warm/90 border-t border-white/8 pt-4" data-testid="verdict-credits">
          <Ticket className="w-4 h-4 text-donkey-orange" />
          One-off verdict credits available: <span className="font-mono text-donkey-orange">{usage.verdict_credits_available}</span>
        </div>
        {hasBilling && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/8 pt-4" data-testid="manage-billing-row">
            <p className="text-sm text-donkey-grey">Update your card, view invoices or cancel — securely on Stripe.</p>
            <Button
              onClick={openBilling}
              disabled={portalBusy}
              className="bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-semibold"
              aria-label="Manage billing on Stripe"
              data-testid="manage-billing-btn"
            >
              {portalBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
              {portalBusy ? "Opening…" : "Manage billing"}
            </Button>
          </div>
        )}
      </div>

      {/* Operational allowances */}
      <h2 className="font-heading font-semibold text-lg text-donkey-warm mb-4">This period’s allowances</h2>
      <p className="text-xs text-donkey-grey mb-4">Resets {fmtDate(usage.reset_at)} · allowances do not roll over.</p>
      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        {OPERATIONAL.map(([k, label]) => (
          <Meter key={k} label={label} used={counts[k] || 0} limit={ent.limits[k]} />
        ))}
      </div>

      {/* Provider-dependent allowances (built; sending/publishing needs a provider) */}
      <h2 className="font-heading font-semibold text-lg text-donkey-warm mb-2">Built — provider setup required</h2>
      <p className="text-xs text-donkey-grey mb-4">These features are built and ready. Sending/publishing is draft/export only until an email or social provider is connected.</p>
      <div className="grid sm:grid-cols-3 gap-4 mb-12">
        {PLANNED.map(([k, label]) => (
          <Meter key={k} label={label} used={counts[k] || 0} limit={ent.limits[k]} planned />
        ))}
      </div>

      <div className="card-donkey rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-donkey-grey">Need more interviews, validation or businesses?</p>
        <Button onClick={() => navigate("/pricing")} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="usage-upgrade-btn">
          View plans <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
